import type {
  Aggregation,
  AnalysisState,
  Comparison,
  DimensionRole,
  FilterOperator,
  FilterTarget,
  Grouping,
  LogicalDataType,
  ScalarValue,
  Selection,
  Sort,
  TimeGrain,
  VisualizationType,
} from '@aibi/contracts';
import {
  inferExpressionType,
  isCalculatedDimension,
  isCalculatedMeasure,
  isGeographicDimension,
  isTemporalDimension,
  type SemanticModelIndex,
} from '@aibi/semantic-model';
import { err, ok, stableHash, type Result } from '@aibi/shared';
import type { AIDiagnostic } from '../errors.js';
import { scanText } from '../guardrails/content-guards.js';
import { DEFAULT_AI_CONTEXT_LIMITS, type AIContextLimits } from '../limits.js';
import { canonicalJson, utf8Length } from './canonical-json.js';

export const AI_CONTEXT_VERSION = 1;

/** Campo visible del modelo tal como lo ve la IA: solo metadatos de negocio. */
export interface AIContextField {
  readonly id: string;
  readonly kind: 'dimension' | 'measure';
  readonly name: string;
  readonly label: string;
  readonly description?: string;
  readonly synonyms: readonly string[];
  readonly dataType: LogicalDataType;
  readonly role?: DimensionRole;
  readonly temporal: boolean;
  readonly geographic: boolean;
  readonly defaultTimeGrain?: TimeGrain;
  readonly calculated: boolean;
  readonly hierarchyId?: string;
  readonly aggregation?: Aggregation;
}

/**
 * Capacidad de visualizacion inyectada como datos (el AI Core no depende del motor de
 * visualizacion). `null` en los maximos significa sin limite.
 */
export interface AIVisualizationCapability {
  readonly type: VisualizationType;
  readonly label: string;
  readonly minDimensions: number;
  readonly maxDimensions: number | null;
  readonly minMeasures: number;
  readonly maxMeasures: number | null;
  readonly requiresTemporal: boolean;
  readonly requiresGeographic: boolean;
}

export interface AIContextAnalysis {
  readonly id: string;
  readonly dimensions: readonly {
    readonly key: string;
    readonly dimensionId: string;
    readonly timeGrain?: TimeGrain;
  }[];
  readonly measures: readonly { readonly key: string; readonly measureId: string }[];
  readonly filters: readonly {
    readonly id: string;
    readonly target: FilterTarget;
    readonly operator: FilterOperator;
    readonly values: readonly ScalarValue[];
  }[];
  readonly sorting: readonly Sort[];
  readonly limit: number | null;
  readonly grouping: Grouping;
  readonly visualization: VisualizationType;
  readonly selections: readonly Selection[];
  readonly drilldown: readonly { readonly hierarchyId: string; readonly levelId: string }[];
  readonly comparison: Pick<Comparison, 'kind' | 'output'> | null;
}

/**
 * Contexto serializable y determinista que se entrega a un proveedor de IA. Por
 * construccion NO contiene: SQL, nombres fisicos de columnas o tablas, credenciales,
 * `secretRefs`, cadenas de conexion, filas de datos, `sampleValues` ni objetos ocultos.
 */
export interface AIContext {
  readonly version: typeof AI_CONTEXT_VERSION;
  readonly locale: string;
  readonly model: {
    readonly id: string;
    readonly name: string;
    readonly description?: string;
    readonly primaryDateDimensionId?: string;
  };
  readonly fields: readonly AIContextField[];
  readonly hierarchies: readonly {
    readonly id: string;
    readonly label: string;
    readonly levels: readonly {
      readonly id: string;
      readonly label: string;
      readonly dimensionId: string;
    }[];
  }[];
  readonly businessRules: readonly {
    readonly id: string;
    readonly name: string;
    readonly description?: string;
  }[];
  readonly dictionary: readonly {
    readonly term: string;
    readonly definition: string;
    readonly relatedObjectIds: readonly string[];
  }[];
  readonly visualizations: readonly AIVisualizationCapability[];
  readonly analysis: AIContextAnalysis | null;
  /** Miembros conocidos por dimension (opcionales y acotados) para resolver filtros. */
  readonly members: readonly {
    readonly dimensionId: string;
    readonly values: readonly (string | number | boolean)[];
  }[];
  /** `true` si algun limite obligo a recortar el contexto. */
  readonly truncated: boolean;
}

export interface BuildAIContextInput {
  readonly index: SemanticModelIndex;
  readonly analysisState: AnalysisState | null;
  readonly visualizations: readonly AIVisualizationCapability[];
  /** Miembros de ejemplo por dimension; nunca muestras crudas de una fuente. */
  readonly members?: Readonly<Record<string, readonly ScalarValue[]>>;
  readonly locale?: string;
}

const MAX_MEMBER_LENGTH = 100;
const MAX_FILTER_VALUES = 50;

/** Texto seguro: recortado y descartado si parece contener SQL, codigo, URLs o secretos. */
function safeText(text: string | undefined, max: number): string | undefined {
  if (text === undefined) return undefined;
  const trimmed = text.trim();
  if (trimmed === '' || scanText(trimmed).length > 0) return undefined;
  return trimmed.length > max ? `${trimmed.slice(0, Math.max(0, max - 1))}…` : trimmed;
}

const optional = <K extends string, V>(key: K, value: V | undefined): Partial<Record<K, V>> =>
  (value === undefined ? {} : { [key]: value }) as Partial<Record<K, V>>;

/**
 * Construye el contexto de IA aplicando redaccion y limites explicitos. Determinista:
 * el mismo modelo, estado y entradas producen exactamente el mismo contexto.
 *
 * @example
 * const context = buildAIContext({ index, analysisState: card, visualizations });
 * if (context.ok) provider.generate({ context: context.value, ... });
 */
export function buildAIContext(
  input: BuildAIContextInput,
  limits: AIContextLimits = DEFAULT_AI_CONTEXT_LIMITS,
): Result<AIContext, AIDiagnostic> {
  const { index, analysisState: state } = input;
  const model = index.model;
  let truncated = false;

  const inAnalysis = new Set<string>([
    ...(state?.dimensions.map((d) => d.dimensionId) ?? []),
    ...(state?.measures.map((m) => m.measureId) ?? []),
    ...(state?.filters.map((f) =>
      f.target.kind === 'dimension' ? f.target.dimensionId : f.target.measureId,
    ) ?? []),
  ]);

  const synonymsOf = (synonyms: readonly string[]): string[] => {
    const safe = synonyms
      .map((synonym) => safeText(synonym, 200))
      .filter((synonym): synonym is string => synonym !== undefined);
    if (safe.length > limits.maxSynonymsPerField) truncated = true;
    return safe.slice(0, limits.maxSynonymsPerField);
  };

  const dimensionFields: AIContextField[] = [];
  for (const dimension of [...model.dimensions, ...model.calculatedDimensions]) {
    if (dimension.hidden) continue;
    const resolved = index.resolveDimension(dimension.id);
    if (!resolved) continue;
    const calculated = isCalculatedDimension(dimension);
    dimensionFields.push({
      id: dimension.id,
      kind: 'dimension',
      name: dimension.name,
      label: dimension.label ?? dimension.name,
      ...optional('description', safeText(dimension.description, limits.maxDescriptionLength)),
      synonyms: synonymsOf(dimension.synonyms),
      dataType: resolved.dataType,
      role: resolved.role,
      temporal: isTemporalDimension(resolved),
      geographic: isGeographicDimension(resolved),
      ...optional('defaultTimeGrain', calculated ? undefined : dimension.defaultTimeGrain),
      calculated,
      ...optional('hierarchyId', dimension.hierarchyId),
    });
  }
  const measureFields: AIContextField[] = [];
  for (const measure of [...model.measures, ...model.calculatedMeasures]) {
    if (measure.hidden) continue;
    const calculated = isCalculatedMeasure(measure);
    measureFields.push({
      id: measure.id,
      kind: 'measure',
      name: measure.name,
      label: measure.label ?? measure.name,
      ...optional('description', safeText(measure.description, limits.maxDescriptionLength)),
      synonyms: synonymsOf(measure.synonyms),
      dataType: inferExpressionType({ type: 'measure', measureId: measure.id }, index),
      temporal: false,
      geographic: false,
      calculated,
      ...optional('aggregation', calculated ? undefined : measure.aggregation),
    });
  }

  const rank = (field: AIContextField): string =>
    `${inAnalysis.has(field.id) ? '0' : '1'}|${field.kind === 'dimension' ? '0' : '1'}|${field.id}`;
  let fields = [...dimensionFields, ...measureFields].sort((a, b) =>
    rank(a) < rank(b) ? -1 : rank(a) > rank(b) ? 1 : 0,
  );
  if (fields.length > limits.maxFields) {
    fields = fields.slice(0, limits.maxFields);
    truncated = true;
  }
  const visibleIds = new Set(fields.map((field) => field.id));

  const hierarchies = model.hierarchies
    .filter((hierarchy) => !hierarchy.hidden)
    .map((hierarchy) => ({
      id: hierarchy.id,
      label: hierarchy.label ?? hierarchy.name,
      levels: hierarchy.levels
        .filter((level) => visibleIds.has(level.dimensionId))
        .map((level) => ({ id: level.id, label: level.name, dimensionId: level.dimensionId })),
    }))
    .filter((hierarchy) => hierarchy.levels.length > 0)
    .sort((a, b) => compareIds(a.id, b.id));

  const enabledRules = model.businessRules.filter((rule) => rule.enabled);
  if (enabledRules.length > limits.maxBusinessRules) truncated = true;
  const businessRules = enabledRules
    .slice()
    .sort((a, b) => compareIds(a.id, b.id))
    .slice(0, limits.maxBusinessRules)
    .map((rule) => ({
      id: rule.id,
      name: safeText(rule.name, 200) ?? rule.id,
      ...optional('description', safeText(rule.description, limits.maxDescriptionLength)),
    }));

  const dictionaryEntries = model.dataDictionary
    .slice()
    .sort((a, b) => compareIds(a.id, b.id))
    .flatMap((entry) => {
      const term = safeText(entry.term, 200);
      const definition = safeText(entry.definition, limits.maxDescriptionLength);
      if (term === undefined || definition === undefined) return [];
      return [
        {
          term,
          definition,
          relatedObjectIds: entry.relatedObjectIds.filter((id) => visibleIds.has(id)).sort(),
        },
      ];
    });
  if (dictionaryEntries.length > limits.maxDictionaryEntries) truncated = true;
  const dictionary = dictionaryEntries.slice(0, limits.maxDictionaryEntries);

  const members: { dimensionId: string; values: (string | number | boolean)[] }[] = [];
  for (const [dimensionId, values] of Object.entries(input.members ?? {}).sort(([a], [b]) =>
    compareIds(a, b),
  )) {
    if (!visibleIds.has(dimensionId) || index.getDimension(dimensionId) === undefined) continue;
    const safe = values.filter(
      (value): value is string | number | boolean =>
        value !== null &&
        (typeof value !== 'string' ||
          (value.length <= MAX_MEMBER_LENGTH && scanText(value).length === 0)),
    );
    if (safe.length > limits.maxMembersPerDimension) truncated = true;
    if (safe.length > 0) {
      members.push({ dimensionId, values: safe.slice(0, limits.maxMembersPerDimension) });
    }
  }

  const visualizations = [...input.visualizations].sort((a, b) => compareIds(a.type, b.type));
  const primaryDate = model.settings.primaryDateDimensionId;

  let context: AIContext = {
    version: AI_CONTEXT_VERSION,
    locale: input.locale ?? model.settings.locale,
    model: {
      id: model.id,
      name: model.name,
      ...optional('description', safeText(model.description, limits.maxDescriptionLength)),
      ...optional(
        'primaryDateDimensionId',
        primaryDate !== undefined && visibleIds.has(primaryDate) ? primaryDate : undefined,
      ),
    },
    fields,
    hierarchies,
    businessRules,
    dictionary,
    visualizations,
    analysis: state ? summarizeAnalysis(state) : null,
    members,
    truncated,
  };

  // Reduccion progresiva y determinista hasta respetar el tamano maximo.
  const fits = (candidate: AIContext): boolean =>
    utf8Length(canonicalJson(candidate)) <= limits.maxSerializedBytes;
  const reductions: ((current: AIContext) => AIContext)[] = [
    (current) => ({ ...current, members: [] }),
    (current) => ({ ...current, dictionary: [] }),
    (current) => ({
      ...current,
      fields: current.fields.map(({ description: _description, ...field }) => field),
      businessRules: current.businessRules.map(({ description: _description, ...rule }) => rule),
    }),
  ];
  for (const reduce of reductions) {
    if (fits(context)) break;
    context = { ...reduce(context), truncated: true };
  }
  while (!fits(context)) {
    const removable = context.fields.filter((field) => !inAnalysis.has(field.id));
    if (removable.length === 0) {
      return err({
        stage: 'input',
        code: 'LIMIT_EXCEEDED',
        path: 'context',
        message: `El contexto supera ${String(limits.maxSerializedBytes)} bytes incluso tras recortarlo.`,
      });
    }
    const keep = Math.floor(removable.length / 2);
    const dropped = new Set(removable.slice(keep).map((field) => field.id));
    context = {
      ...context,
      fields: context.fields.filter((field) => !dropped.has(field.id)),
      truncated: true,
    };
  }
  return ok(context);
}

function summarizeAnalysis(state: AnalysisState): AIContextAnalysis {
  return {
    id: state.id,
    dimensions: state.dimensions.map((d) => ({
      key: d.key,
      dimensionId: d.dimensionId,
      ...optional('timeGrain', d.timeGrain),
    })),
    measures: state.measures.map((m) => ({ key: m.key, measureId: m.measureId })),
    filters: state.filters.map((f) => ({
      id: f.id,
      target: f.target,
      operator: f.operator,
      values: f.values.slice(0, MAX_FILTER_VALUES),
    })),
    sorting: state.sorting,
    limit: state.limit,
    grouping: state.grouping,
    visualization: state.visualization.type,
    selections: state.selections.map((s) => ({
      dimensionId: s.dimensionId,
      values: s.values.slice(0, MAX_FILTER_VALUES),
    })),
    drilldown: state.drilldown.map((d) => ({ hierarchyId: d.hierarchyId, levelId: d.levelId })),
    comparison: state.comparison
      ? { kind: state.comparison.kind, output: state.comparison.output }
      : null,
  };
}

const compareIds = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

/** Texto canonico del contexto: es exactamente lo que se envia a un proveedor. */
export const serializeAIContext = (context: AIContext): string => canonicalJson(context);

/** Huella estable del contexto (trazabilidad y cache). */
export const aiContextFingerprint = (context: AIContext): string =>
  stableHash(serializeAIContext(context), 8);
